/**
 * AI Property Review Component
 * Displays AI-generated property review/overview
 */

import { skipToken } from "@reduxjs/toolkit/query";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { tokenStorage } from "../../config/api";
import { useGetPropertyReviewMutation } from "../../store/api/aiChatApi";
import { useGetProfileQuery } from "../../store/api/authApi";
import Icon from "../AppIcon";
import Button from "../ui/Button";
import Loader from "../ui/Loader";

interface AIPropertyReviewProps {
	propertyId: string;
	propertyName?: string;
}

const AIPropertyReview: React.FC<AIPropertyReviewProps> = ({
	propertyId,
	propertyName,
}) => {
	const navigate = useNavigate();
	// Only fetch profile if user has a token (skip query if no token to avoid 403 errors)
	const accessToken = tokenStorage.getAccessToken();
	const {
		data: currentUser,
		isLoading: isLoadingProfile,
		refetch: refetchProfile,
	} = useGetProfileQuery(accessToken ? undefined : skipToken);
	const [getReview, { isLoading, isError }] = useGetPropertyReviewMutation();
	const [review, setReview] = useState<string | null>(null);
	const [hasRequested, setHasRequested] = useState(false);

	// Refetch profile when access token changes (e.g., after login)
	useEffect(() => {
		if (accessToken) {
			refetchProfile();
		}
	}, [accessToken, refetchProfile]);

	const handleGetReview = async () => {
		if (hasRequested) return;

		setHasRequested(true);
		try {
			const response = await getReview({ property_id: propertyId }).unwrap();
			if (response.success && response.review) {
				setReview(response.review);
			}
		} catch (error: any) {
			console.error("Error fetching property review:", error);
			setHasRequested(false);
		}
	};

	if (isError && !review) {
		return (
			<div className="bg-card border border-border rounded-xl p-6">
				<div className="flex items-center justify-center py-8">
					<div className="text-center">
						<Icon
							name="AlertCircle"
							size={48}
							className="text-error mx-auto mb-4"
						/>
						<p className="text-muted-foreground">
							Unable to generate AI review at this time. Please try again later.
						</p>
					</div>
				</div>
			</div>
		);
	}

	// Show loading state while checking authentication
	if (isLoadingProfile && accessToken) {
		return (
			<div className="bg-card border border-border rounded-xl p-6">
				<div className="flex items-center justify-center py-8">
					<div className="text-center">
						<Icon
							name="Loader2"
							size={24}
							className="text-primary animate-spin mx-auto mb-2"
						/>
						<p className="text-sm text-muted-foreground">Loading...</p>
					</div>
				</div>
			</div>
		);
	}

	// Show sign-in prompt if user is not authenticated (no token and no user)
	if (!accessToken && !currentUser) {
		return (
			<div className="bg-card border border-border rounded-xl p-6">
				<div className="flex items-center justify-between mb-4">
					<div className="flex items-center space-x-3">
						<div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
							<Icon name="Sparkles" size={24} className="text-primary" />
						</div>
						<div>
							<h3 className="text-xl font-semibold text-foreground">
								AI Property Overview
							</h3>
							<p className="text-sm text-muted-foreground">
								Sign in to get an AI-powered analysis of this property
							</p>
						</div>
					</div>
				</div>
				<Button
					onClick={() =>
						navigate(
							`/login?redirect=${encodeURIComponent(
								window.location.pathname + window.location.search,
							)}`,
						)
					}
					iconName="LogIn"
					iconPosition="left"
					className="w-full"
				>
					Sign In to View AI Overview
				</Button>
			</div>
		);
	}

	// Show generate button if user is authenticated (has token or user) but hasn't requested review yet
	if (!review && !hasRequested && (currentUser || accessToken)) {
		return (
			<div className="bg-card border border-border rounded-xl p-6">
				<div className="flex items-center justify-between mb-4">
					<div className="flex items-center space-x-3">
						<div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
							<Icon name="Zap" size={24} className="text-primary" />
						</div>
						<div>
							<h3 className="text-xl font-semibold text-foreground">
								AI Property Overview
							</h3>
							<p className="text-sm text-muted-foreground">
								Get an AI-powered analysis of this property
							</p>
						</div>
					</div>
				</div>
				<Button
					onClick={handleGetReview}
					disabled={isLoading}
					iconName="Sparkles"
					iconPosition="left"
					className="w-full"
				>
					{isLoading ? "Generating Review..." : "Generate AI Overview"}
				</Button>
			</div>
		);
	}

	if (isLoading) {
		return (
			<div className="bg-card border border-border rounded-xl p-6">
				<div className="flex items-center justify-center py-8">
					<div className="text-center">
						<Icon
							name="Loader2"
							size={48}
							className="text-primary animate-spin mx-auto mb-4"
						/>
						<p className="text-muted-foreground">
							Generating AI-powered property overview...
						</p>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="bg-card border border-border rounded-xl p-6">
			<div className="flex items-center justify-between mb-4">
				<div className="flex items-center space-x-3">
					<div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
						<Icon name="Sparkles" size={24} className="text-primary" />
					</div>
					<div>
						<h3 className="text-xl font-semibold text-foreground">
							AI Property Overview
						</h3>
						{propertyName && (
							<p className="text-sm text-muted-foreground">{propertyName}</p>
						)}
					</div>
				</div>
				<Button
					variant="ghost"
					size="sm"
					onClick={handleGetReview}
					disabled={isLoading}
					iconName="RefreshCw"
				>
					Regenerate
				</Button>
			</div>
			<div className="prose prose-sm max-w-none">
				<div className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
					{review || "No review available."}
				</div>
			</div>
			<div className="mt-4 pt-4 border-t border-border">
				<p className="text-xs text-muted-foreground flex items-center">
					<Icon name="Info" size={14} className="mr-1" />
					This overview is generated by AI and may not reflect all property
					details. Please verify information with the property manager.
				</p>
			</div>
		</div>
	);
};

export default AIPropertyReview;
